import { Dialog, DialogActions, DialogContent, FormControl, FormLabel, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import ButtonDelete from "components/Buttons/ButtonDelete";
import ButtonSave from "components/Buttons/ButtonSave";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonDelete from "components/Buttons/IconButtonDelete";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconTrophy from "components/Icons/IconTrophy";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPaper from "components/MuiExtras/MuiPaper";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldNumeric from "components/TextFieldNumeric";
import AppMessage from "components/Translations/AppMessage";
import LibUtils from "lib/LibUtils";
import { GetAwardTopicExceptionDto, PagedResult, SaveAwardTopicExceptionDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import TopicAutoComplete from "views/Topic/Components/TopicAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

const MaxTakeRecords = 10;

enum FormMode { Add, Edit, List }

interface IProps {
    initialMode: "create" | "list";
    onClose?: (counter: number) => void;
}

interface IState {
    list: PagedResult<GetAwardTopicExceptionDto>;
    mode: FormMode;
    mouseOver: number;
    currentItem: GetAwardTopicExceptionDto;
    offset: number;
    formCycle: RequestLifeCycle;
    lastError: any;
    validate: {
        quizNewSessionMaxPoints: boolean;
        quizRevisionMaxPoints: boolean;
        topicId: boolean;
    };
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class AwardTopicDialog extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            list: null,
            offset: 0,

            currentItem: null,
            mode: FormMode.List,
            mouseOver: null,
            formCycle: RequestLifeCycle.None,
            lastError: null,
            validate: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Gamification.Components.AwardTopicDialog." + r, f);
    }

    ///
    private handleClose = () => {
        if (this.props.onClose)
            this.props.onClose(this.state.list ? this.state.list.total : null);
    };

    ///
    private handleEnterEditMode(item: GetAwardTopicExceptionDto) {
        this.setState({
            mode: FormMode.Edit,
            currentItem: item,
            validate: {
                quizNewSessionMaxPoints: true,
                quizRevisionMaxPoints: true,
                topicId: true
            }
        });
    }

    ///
    private handleEnterAddMode() {
        this.setState({
            mode: FormMode.Add,
            formCycle: RequestLifeCycle.None,
            currentItem: {
                name: "",
                topicId: null,
                quizNewSessionMaxPoints: 1,
                quizRevisionMaxPoints: 1
            },
            validate: {
                quizNewSessionMaxPoints: true,
                quizRevisionMaxPoints: true,
                topicId: false
            }
        });
    }

    ///
    private loadList(offset: number) {
        this.setState({ list: null, formCycle: RequestLifeCycle.Loading, mode: FormMode.List });

        api.getAwardTopicExceptions({ offset, take: MaxTakeRecords })
            .then((result) => {
                this.setState({ list: result, offset: offset, formCycle: RequestLifeCycle.None });
            })
            .catch((error) => {
                this.setState({ list: null, offset: 0, lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private renderPager() {
        const { list } = this.state;

        if (list == null || list.total == 0)
            return null;

        return (
            <MuiPagination
                limit={MaxTakeRecords}
                offset={this.state.offset}
                total={this.state.list.total}
                innerButtonCount={3}
                onClick={(e, offset) => this.loadList(offset)}
            />
        );
    }

    ///
    private renderListItems() {
        const { list } = this.state;

        return (
            list.items.map((item, index) => {
                return (
                    <MuiContainer key={index} margin="normal">
                        <Grid
                            container
                            spacing={1}
                            justifyContent="space-between"
                            alignItems="center"
                            wrap="nowrap"
                            className={this.state.mouseOver == index ? GlobalClassKeys.overElementBackground : null}
                            onMouseEnter={() => this.setState({ mouseOver: index })}
                            onMouseLeave={() => this.setState({ mouseOver: null })}
                        >
                            <GridEx item xs zeroMinWidth alignSelf="center">
                                <MuiLink
                                    color="secondary"
                                    href={null}
                                    onClick={() => this.handleEnterEditMode(item)}
                                >
                                    {item.name}
                                </MuiLink>
                                <br />
                                <MuiColor variant="muted">
                                    {this.res("NewSessionMaxPointsText", { points: item.quizNewSessionMaxPoints })}
                                    <br />
                                    {this.res("RevisionMaxPointsText", { points: item.quizRevisionMaxPoints })}
                                </MuiColor>
                            </GridEx>
                            <Grid item>
                                <IconButtonDelete
                                    size="small"
                                    onClick={() => this.handleDelete(item.topicId)} />
                            </Grid>
                        </Grid>
                    </MuiContainer>
                );
            })
        );
    }

    ///
    private renderList() {
        const { list, formCycle } = this.state;

        return (
            <Dialog
                open
                maxWidth="md"
                fullWidth
            >
                <MuiDialogTitleClose
                    onClose={this.handleClose}
                    title={

                        <Grid container alignItems="center">
                            <Grid item>
                                <IconButtonAdd
                                    onClick={() => { this.handleEnterAddMode(); }}
                                />
                            </Grid>
                            <Grid item xs>
                                <span>{this.res("ListHeaderTitle")}</span>
                            </Grid>
                        </Grid>
                    }
                />

                <DialogContent >
                    <MuiPaper dense fullHeight >
                        {
                            formCycle == RequestLifeCycle.LoadFailed
                                ? <RequestErrorReport error={this.state.lastError} />
                                : list == null
                                    ? <SpinnerIndicator />
                                    : list.total == 0
                                        ? <Typography variant="h6">{this.res("ListEmptyData")}</Typography>
                                        : this.renderListItems()
                        }
                    </MuiPaper>
                </DialogContent>

                <DialogActions>
                    {this.renderPager()}
                </DialogActions>

            </Dialog>

        );
    }

    ///
    private handleSave = () => {
        const { currentItem } = this.state;

        this.setState({ formCycle: RequestLifeCycle.Posting });

        const dto: SaveAwardTopicExceptionDto = {
            topicId: currentItem.topicId,
            quizNewSessionMaxPoints: currentItem.quizNewSessionMaxPoints,
            quizRevisionMaxPoints: currentItem.quizRevisionMaxPoints
        };

        api.saveAwardTopicException(dto)
            .then((result) => {
                if (result) {
                    if (result) {
                        if (this.props.initialMode == "list")
                            this.loadList(this.state.offset);
                        else {
                            if (this.props.onClose)
                                this.props.onClose(1);
                        }
                    }
                }
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    };

    ///
    private handleDelete(topicId: string) {
        this.setState({ formCycle: RequestLifeCycle.Posting });

        api.deleteAwardTopicException(topicId)
            .then((result) => {
                if (result) this.loadList(this.state.offset);
                else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "topic":
                    state.currentItem.topicId = v.key;
                    state.currentItem.name = v.label;
                    state.validate.topicId = v != null && v != "";
                    break;

                case "quizNewSessionMaxPoints":
                    state.currentItem.quizNewSessionMaxPoints = v;
                    state.validate.quizNewSessionMaxPoints = v > 0;
                    break;

                case "quizRevisionMaxPoints":
                    state.currentItem.quizRevisionMaxPoints = v;
                    state.validate.quizRevisionMaxPoints = v > 0;
                    break;
            }
            return state;
        });
    }

    ///
    private formIsValid() {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private handleCloseEdit = () => {
        if (this.props.initialMode == "list")
            this.setState({ mode: FormMode.List });
        else
            this.handleClose();
    };

    ///
    private renderForm() {
        const { formCycle, lastError, currentItem, mode, validate } = this.state;
        return (
            <Dialog
                open
                maxWidth="md"
                fullWidth
            >
                <MuiDialogTitleClose
                    onClose={this.handleCloseEdit}
                    subtitle={mode == FormMode.Edit ? <MuiColor variant="warning">{currentItem.name}</MuiColor> : null}
                    title={this.res(mode == FormMode.Add ? "FormNewTitle" : "FormEditTitle")}
                />

                <DialogContent>
                    <MuiPaper dense fullHeight >

                        {formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                        <RequestErrorDialog
                            open={formCycle == RequestLifeCycle.PostFailed}
                            error={lastError}
                            onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                        />

                        <MuiContainer margin="normal">

                            {
                                mode == FormMode.Add &&
                                <TopicAutoComplete
                                    margin="dense"
                                    multiple={false}
                                    overModal
                                    placeholder={this.props.t("Common.Literals.TopicSelection")}
                                    selectedItems={currentItem.topicId == null
                                        ? null
                                        : [{
                                            key: currentItem.topicId,
                                            label: currentItem.name,
                                            canDelete: true,
                                            hasMemberRestriction: null,
                                            thumbnailUrl: null,
                                        }]
                                    }
                                    onChange={
                                        (topics) => {
                                            this.updateRecord("topic", topics.length == 0 ? null : topics[0]);
                                        }
                                    }
                                />
                            }

                            <FormControl margin="dense">
                                <FormLabel htmlFor="quizNewSessionMaxPoints">{this.props.t("Views.Gamification.Components.AwardEdit.GrantNewSessionLabel")}</FormLabel>
                                <TextFieldNumeric
                                    name="quizNewSessionMaxPoints"
                                    allowNegative={false}
                                    error={validate.quizNewSessionMaxPoints === false}
                                    margin="dense"
                                    value={currentItem.quizNewSessionMaxPoints}
                                    onChange={this.handleChange}
                                    InputProps={
                                        {
                                            startAdornment: <IconTrophy fixedWidth />
                                        }
                                    }
                                    required
                                />
                            </FormControl>

                            <br />

                            <FormControl margin="dense">
                                <FormLabel htmlFor="quizRevisionMaxPoints">{this.props.t("Views.Gamification.Components.AwardEdit.GrantSessionRevisionLabel")}</FormLabel>
                                <TextFieldNumeric
                                    name="quizRevisionMaxPoints"
                                    allowNegative={false}
                                    error={validate.quizRevisionMaxPoints === false}
                                    margin="dense"
                                    value={currentItem.quizRevisionMaxPoints}
                                    onChange={this.handleChange}
                                    InputProps={
                                        {
                                            startAdornment: <IconTrophy fixedWidth />
                                        }
                                    }
                                    required
                                />
                            </FormControl>
                        </MuiContainer>

                    </MuiPaper>
                </DialogContent>

                <DialogActions>
                    <MuiPushRight>
                        {
                            mode == FormMode.Edit &&
                            <ButtonDelete onClick={() => this.handleDelete(currentItem.topicId)} />
                        }

                        <ButtonSave
                            disabled={!this.formIsValid()}
                            onClick={this.handleSave} />

                    </MuiPushRight>
                </DialogActions>

            </Dialog>
        );
    }

    ///
    public componentDidMount() {
        if (this.props.initialMode == "list")
            this.loadList(0);
        else if (this.props.initialMode == "create")
            this.handleEnterAddMode();
    }

    ///
    public render() {
        const { mode } = this.state;

        return (mode == FormMode.List)
            ? this.renderList()
            : this.renderForm();
    }
}

export default withSnackbar(withTranslation()(AwardTopicDialog));